/**
 * @fileoverview Badge Design System Types
 *
 * Comprehensive type definitions for the LOCI Event Card Badge system,
 * including visual specifications, iconography, copy, and accessibility.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */

import type { Coordinates } from './common';

/**
 * Badge type enumeration
 * Represents all possible badge states for event cards
 */
export type BadgeType =
  | 'live-now'
  | 'sold-out'
  | 'almost-full'
  | 'canceled'
  | 'featured'
  | 'new'
  | 'near-you'
  | 'online'
  | 'registered'
  | 'recurring';

/**
 * Badge variant - maps to color schemes
 */
export type BadgeVariant = 'primary' | 'secondary' | 'accent' | 'warning' | 'success';

/**
 * Badge size options
 */
export type BadgeSize = 'compact' | 'standard' | 'prominent';

/**
 * Badge style types
 */
export type BadgeStyle = 'filled' | 'outlined' | 'ghost';

/**
 * Icon source libraries
 */
export type IconSource = 'lucide' | 'custom' | 'heroicons';

/**
 * Icon weight options
 */
export type IconWeight = 'light' | 'regular' | 'bold';

/**
 * Badge appearance configuration
 */
export interface BadgeAppearance {
  /** Color scheme variant */
  variant: BadgeVariant;
  /** Badge size */
  size: BadgeSize;
  /** Visual style */
  style: BadgeStyle;
  /** Border radius token */
  borderRadius: 'rounded-sm' | 'rounded-md' | 'rounded-lg' | 'rounded-full';
  /** Typography token reference */
  typography: string;
  /** Elevation/shadow token */
  elevation: string;
}

/**
 * Badge color scheme with accessibility
 */
export interface BadgeColorScheme {
  /** Background color */
  background: string;
  /** Foreground/text color */
  foreground: string;
  /** Border color (optional) */
  border?: string;
  /** Hover state color */
  hover: string;
  /** Focus state color */
  focus: string;
  /** Color contrast ratio (minimum 4.5:1 for WCAG AA) */
  contrast: number;
}

/**
 * Badge icon configuration
 */
export interface BadgeIcon {
  /** Icon name/identifier */
  name: string;
  /** Icon size in pixels */
  size: 12 | 16 | 20;
  /** Icon source library */
  source: IconSource;
  /** Accessible alt text */
  alt: string;
  /** Icon weight/thickness */
  weight: IconWeight;
}

/**
 * Badge copy/messaging configuration
 */
export interface BadgeCopy {
  /** Primary badge text */
  primary: string;
  /** Compact/mobile version */
  short: string;
  /** Screen reader text */
  aria: string;
  /** Extended tooltip text (optional) */
  tooltip?: string;
  /** i18n translation key */
  i18n: string;
}

/**
 * Badge animation configuration
 */
export interface BadgeAnimationConfig {
  /** Pulse animation (for live badges) */
  pulse: boolean;
  /** Fade in animation */
  fadeIn: boolean;
  /** Bounce animation (for interactions) */
  bounce: boolean;
  /** Animation duration in ms */
  duration: number;
  /** Respect user motion preferences */
  respectMotionPreference: boolean;
}

/**
 * Badge accessibility properties
 */
export interface BadgeA11yProps {
  /** ARIA role */
  role: 'status' | 'img' | 'button';
  /** ARIA label */
  'aria-label': string;
  /** ARIA described by reference */
  'aria-describedby'?: string;
  /** ARIA live region */
  'aria-live'?: 'polite' | 'assertive';
  /** Tab index for keyboard navigation */
  tabIndex?: number;
}

/**
 * Complete badge configuration
 */
export interface BadgeConfig {
  /** Badge type identifier */
  type: BadgeType;
  /** Visual appearance settings */
  appearance: BadgeAppearance;
  /** Color scheme */
  colors: BadgeColorScheme;
  /** Icon configuration */
  icon: BadgeIcon;
  /** Copy/messaging */
  copy: BadgeCopy;
  /** Animation settings */
  animation: BadgeAnimationConfig;
  /** Accessibility properties */
  a11y: BadgeA11yProps;
  /** Priority for badge stacking (1-10, higher = more important) */
  priority: number;
}

/**
 * Badge display props for React components
 */
export interface EventBadgeProps {
  /** Badge type */
  type: BadgeType;
  /** Badge size override */
  size?: BadgeSize;
  /** Show icon */
  showIcon?: boolean;
  /** Show text */
  showText?: boolean;
  /** Enable animations */
  animated?: boolean;
  /** Show tooltip on hover */
  showTooltipOnHover?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Custom style overrides */
  style?: Record<string, any>;
  /** Click handler (for interactive badges) */
  onClick?: () => void;
}

/**
 * Badge registry entry
 * Maps badge types to their complete configurations
 */
export type BadgeRegistry = Record<BadgeType, BadgeConfig>;

/**
 * Badge context for conditional display
 */
export interface BadgeContext {
  /** Current timestamp */
  now: Date;
  /** Event start time */
  eventStartTime: Date;
  /** Event end time */
  eventEndTime?: Date;
  /** Available tickets count */
  availableTickets?: number;
  /** Total capacity */
  totalCapacity?: number;
  /** User registration status */
  isRegistered?: boolean;
  /** User location */
  userLocation?: Coordinates;
  /** Event location */
  eventLocation: Coordinates;
  /** Distance threshold for "near you" badge (in meters) */
  nearThreshold?: number;
  /** Event featured status */
  isFeatured?: boolean;
  /** Event creation date */
  createdAt?: Date;
  /** "New" badge threshold (in days) */
  newThreshold?: number;
  /** Event format (in-person/online) */
  isOnline?: boolean;
  /** Event recurring status */
  isRecurring?: boolean;
  /** Event cancellation status */
  isCanceled?: boolean;
  /** Sold out status */
  isSoldOut?: boolean;
}

/**
 * Badge evaluation result
 * Contains the badges that should be displayed for an event
 */
export interface BadgeEvaluationResult {
  /** Primary badge (highest priority) */
  primary: BadgeType | null;
  /** Secondary badges (up to 2) */
  secondary: BadgeType[];
  /** All applicable badge types */
  all: BadgeType[];
  /** Context used for evaluation */
  context: BadgeContext;
}
